import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './ScoutIQDashboard.css';
import '../../pages/CourseDetailCalendar.css';
import CoursesColumn from './CoursesColumn';
import CenterColumn from './CenterColumn';
// eslint-disable-next-line no-unused-vars
import ScoutsDeskChat from './ScoutsDeskChat';
import { normalizeToolKey, TOOL_KEY_SET } from '../../constants/toolKeys';
import { FEATURE_KEY_TERMS } from '../../config/featureFlags';
import { IS_DEV } from '../../utils/envDetection';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import { cn } from '../../utils/classNames';
// subscription gating handled by child components
// Reuse existing document components for vanilla documents tab
import { FileUpload, MyDocumentsCard } from '..';
import DocumentPreview from '../DocumentPreview';
import CourseNotesContainer from '../notes/CourseNotesContainer';
import Calendar from '../calendar/Calendar';
// Vanilla (non-React) course view helpers – pure DOM tab system
import { setupVanillaCourseView, showScoutIQCourseView } from './scoutiqCourseView';
import { useData } from '../../contexts/DataContext';
// Modal components for assignment and event details
import CenteredModal from '../ui/CenteredModal';
import AssignmentDetails from '../assignments/AssignmentDetails';
import EventDetails from '../events/EventDetails';
import EventForm from '../forms/EventForm/EventForm';
import AssignmentForm from '../forms/AssignmentForm/AssignmentForm';
import AddToCalendarModal from '../calendar/AddToCalendarModal/AddToCalendarModal';
import { normalizeEventForModal } from './CenterColumn';
import CourseDetails from '../courses/CourseDetails';
import UnifiedActivities from '../activities/UnifiedActivities';
import AiChatCard from '../aiChat/AiChatCard';
// Workspace layout hook for collapse/expand functionality
import { useWorkspaceLayout } from '../../hooks/useWorkspaceLayout';
import { setupWorkspaceShortcuts } from '../../utils/keyboardShortcuts';
// DEPRECATED: Replaced by unified PanelEdgeControl component
// import CollapsedCoursesTab from './CollapsedCoursesTab';
// import CollapsedDeskTab from './CollapsedDeskTab';
// eslint-disable-next-line no-unused-vars
import PanelEdgeControl from '../layout/PanelEdgeControl';
// eslint-disable-next-line no-unused-vars
import DocsRequiredIndicator from './DocsRequiredIndicator';
import SyllabusScanBanner from './SyllabusScanBanner';

// ScoutToolsRow: Reusable tools buttons component
// eslint-disable-next-line no-unused-vars
function ScoutToolsRow() {
  return (
    <div className="scoutiq-tools-row" data-tour="scouts-desk-tools">
      <button className="scout-tool-btn" data-tool="chat">
        <div className="tool-icon">💬</div>
        <div className="tool-label">Chat</div>
      </button>
      <button className="scout-tool-btn" data-tool="quizzes">
        <div className="tool-icon">🧠</div>
        <div className="tool-label">Quizzes</div>
      </button>
      <button className="scout-tool-btn" data-tool="flashcards">
        <div className="tool-icon">🏷️</div>
        <div className="tool-label">Flashcards</div>
      </button>
      <button className="scout-tool-btn" data-tool="podcasts">
        <div className="tool-icon">🎙️</div>
        <div className="tool-label">Podcasts</div>
      </button>
      <button className="scout-tool-btn" data-tool="audiobooks">
        <div className="tool-icon">📖</div>
        <div className="tool-label">Audiobooks</div>
      </button>
      <button className="scout-tool-btn" data-tool="tts">
        <div className="tool-icon">🔊</div>
        <div className="tool-label">Text to Speech</div>
      </button>
      {FEATURE_KEY_TERMS && (
        <button className="scout-tool-btn" data-tool="key_terms">
          <div className="tool-icon">🔑</div>
          <div className="tool-label">Key Terms</div>
        </button>
      )}
    </div>
  );
}

// ScoutIQDashboard
// Lightweight layout shell: 3 columns (Courses | Intelligence | Scout's Desk).
// Fetching & feature logic live in child components; this file only wires sizing,
// selection, and documents portal mounting.

export default function ScoutIQDashboard({
  courses = [],
  loadingCourses = false,
  selectedCourseId = null,
  selectedCourse = null,
  onSelectCourse,
  highlightCourseId = null,
  selectedCourseAssignments = [], // external prefetch override (optional)
  selectedCourseEvents = [],       // external prefetch override (optional)
  selectedCourseSummary = null,    // TODO: Rich AI summary object
  userName = 'Student',            // TODO: Pass real user name from auth/user context
  upcomingItems = []               // TODO: Provide unified upcoming items array for home state
}) {
  // Stabilize courses prop to prevent unnecessary re-renders
  const stableCourses = useMemo(() => courses, [courses]);
  
  // Chat expansion state: when true, chat spans center column and touches courses column boundary.
  const [, setIsChatExpanded] = useState(false); // eslint-disable-line no-unused-vars
  // eslint-disable-next-line no-unused-vars
  const handleToggleChatExpand = useCallback(() => setIsChatExpanded(e => !e), []);
  
  // Scout's Desk super-expansion: when true, desk spans center column (like chat expansion)
  const [, setIsDeskSuperExpanded] = useState(false); // eslint-disable-line no-unused-vars
  
  // Store timeline updaters ref for passing to create modals
  const timelineUpdatersRef = useRef(null);
  const handleTimelineReady = useCallback((updaters) => {
    timelineUpdatersRef.current = updaters;
  }, []);

  // Workspace layout hook for collapse/expand functionality
  const {
    effectiveCoursesCollapsed,
    effectiveDeskCollapsed,
    useDrawerMode: _useDrawerMode, // eslint-disable-line no-unused-vars
    activeDrawer: _activeDrawer, // eslint-disable-line no-unused-vars
    transitionState,
    isResizingCourses: _isResizingCourses, // eslint-disable-line no-unused-vars
    isResizingDesk: _isResizingDesk, // eslint-disable-line no-unused-vars
    toggleCourses,
    toggleDesk,
    closeDrawer: _closeDrawer, // eslint-disable-line no-unused-vars
    focusTargetRefs,
    prefersReducedMotion,
    viewportWidth
  } = useWorkspaceLayout();

  // Refs for syllabus maximize bridge (avoid stale closures) — must be after useWorkspaceLayout
  const coursesCollapsedRef = useRef(effectiveCoursesCollapsed);
  const toggleCoursesRef = useRef(toggleCourses);
  
  // Ultra-wide breakpoint: Scout's Desk only renders at >= 1600px
  const ULTRAWIDE_MIN = 1600;
  const _isUltraWide = viewportWidth >= ULTRAWIDE_MIN; // eslint-disable-line no-unused-vars

  // Keep syllabus maximize bridge refs current
  useEffect(() => {
    coursesCollapsedRef.current = effectiveCoursesCollapsed;
  }, [effectiveCoursesCollapsed]);
  useEffect(() => {
    toggleCoursesRef.current = toggleCourses;
  }, [toggleCourses]);

  // Listen for syllabus maximize-mode collapse/expand requests
  useEffect(() => {
    const handler = (e) => {
      const shouldCollapse = e.detail?.collapse;
      const isCollapsed = coursesCollapsedRef.current;
      if (shouldCollapse && !isCollapsed) {
        toggleCoursesRef.current();
      } else if (!shouldCollapse && isCollapsed) {
        toggleCoursesRef.current();
      }
    };
    window.addEventListener('scoutiq:collapseCourses', handler);
    return () => window.removeEventListener('scoutiq:collapseCourses', handler);
  }, []);
  
  // Setup keyboard shortcuts for collapse/expand
  useEffect(() => {
    const cleanup = setupWorkspaceShortcuts({
      onToggleCourses: toggleCourses,
      onToggleDesk: toggleDesk
    });
    return cleanup;
  }, [toggleCourses, toggleDesk]);
  
  // Listen for Scout's Desk super-expand event
  useEffect(() => {
    const handleSuperExpand = (e) => {
      console.log('Super expand desk event received');
      // Toggle Scout's Desk super expansion (spanning center column)
      setIsDeskSuperExpanded(prev => {
        console.log('Toggling desk super expand from', prev, 'to', !prev);
        return !prev;
      });
    };

    window.addEventListener('scoutiq:super-expand-desk', handleSuperExpand);

    // Also listen for tutorial-driven request to open the Scout's Desk tools
    const handleOpenDeskFromTutorial = (e) => {
      try {
        // Ensure tools panel is open and select default tool (chat)
        setIsToolsOpen(true);
        setActiveTool('chat');

        // If the desk is currently collapsed, call toggleDesk to expand it.
        // Only call toggleDesk when it's a function and the desk is collapsed to avoid unintended toggles.
        try {
          if (typeof toggleDesk === 'function' && effectiveDeskCollapsed) {
            toggleDesk();
          }
        } catch (err) {
          console.warn('Error toggling desk open from tutorial event', err);
        }
      } catch (err) {
        console.warn('Failed to handle scoutiq:openScoutsDesk event', err);
      }
    };
    window.addEventListener('scoutiq:openScoutsDesk', handleOpenDeskFromTutorial);

    return () => {
      window.removeEventListener('scoutiq:super-expand-desk', handleSuperExpand);
      window.removeEventListener('scoutiq:openScoutsDesk', handleOpenDeskFromTutorial);
    };
  }, [toggleDesk, effectiveDeskCollapsed]);
  
  // ARIA live region state for screen reader announcements
  const [liveMessage, setLiveMessage] = useState('');
  
  // Tools visibility state - local to ScoutIQ Dashboard
  const [, setIsToolsOpen] = useState(false); // eslint-disable-line no-unused-vars
  
  // Tool routing state
  const [activeTool, setActiveTool] = useState("chat"); // "chat" | "quizzes" | "flashcards" etc.
  
  // Migrate toolStates from PascalCase to lowercase keys
  const migrateToolStates = useCallback((prev) => {
    if (!prev || typeof prev !== 'object') return {};
    
    const migrationMap = {
      Quizzes: "quizzes",
      Flashcards: "flashcards",
      Podcasts: "podcasts",
      Audiobooks: "audiobooks",
      KeyTerms: "key_terms",
      "Key Terms": "key_terms"
    };
    
    const next = { ...prev };
    for (const [oldKey, newKey] of Object.entries(migrationMap)) {
      if (prev[oldKey] && !prev[newKey]) {
        next[newKey] = prev[oldKey];
      }
    }
    // TODO: remove PascalCase toolStates keys after 1-2 releases once all references are migrated
    return next;
  }, []);
  
  const [, setToolStates] = useState(() => migrateToolStates({})); // eslint-disable-line no-unused-vars
  
  // Tool state update handler
  // eslint-disable-next-line no-unused-vars
  const handleToolStateChange = useCallback((toolName, state) => {
    setToolStates(prev => ({
      ...prev,
      [toolName]: state
    }));
  }, []);
  
  // Sanitize activeTool - normalize legacy values and redirect disabled features
  useEffect(() => {
    const normalized = normalizeToolKey(activeTool);

    // Redirect to chat if tool is unknown or disabled
    if (!TOOL_KEY_SET.has(normalized)) {
      if (IS_DEV) {
        console.warn(`[ScoutIQ] Unknown tool "${activeTool}", redirecting to chat`);
      }
      if (activeTool !== 'chat') setActiveTool('chat');
      return;
    }

    // Redirect Key Terms to chat if feature flag is disabled
    if (normalized === 'key_terms' && !FEATURE_KEY_TERMS) {
      if (IS_DEV) {
        console.warn('[ScoutIQ] Key Terms is disabled by feature flag; redirecting to chat');
      }
      if (activeTool !== 'chat') setActiveTool('chat');
      return;
    }

    // Apply normalization if needed
    if (normalized !== activeTool) {
      setActiveTool(normalized);
    }
  }, [activeTool]);
  
  // Portal mount state to force re-render when documents container becomes available
  const [portalMountKey, setPortalMountKey] = useState(0);
  const [activeCourseTab, setActiveCourseTab] = useState('description'); // Track active course tab
  const [syllabusTabVisitedCourses, setSyllabusTabVisitedCourses] = useState(new Set()); // Track which courses have had their syllabus tab visited

  // Document preview state (similar to syllabus preview)
  // Modal state for assignment and event details from vanilla JS cards
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Modal state for creating new events/assignments
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [showCourseCalendarAdd, setShowCourseCalendarAdd] = useState(false);
  const [createModalCourseId, setCreateModalCourseId] = useState(null);

  // Navigation hook for document clicks
  const navigate = useNavigate();

  // Handle document click from MyDocumentsCard — navigate to Documents page
  const handleDocumentClick = useCallback((documentId, documentName) => {
    // Track document view
    UsageEventsTracker.recordUserAction('CONTENT_VIEW', {
      action: 'view',
      category: 'documents',
      entityType: 'document',
      entityId: documentId,
      label: documentName,
      metadata: { source: 'scout_documents_panel' }
    });

    // Navigate to the Documents page with the selected document
    navigate(`/documents?doc=${documentId}`);
  }, [navigate]);

  // Vanilla course view tab system (pure DOM). Rebind on courses change.
  useEffect(() => { setupVanillaCourseView(); }, [courses]);

  // === DataContext integration: load full events & assignments for selected course ===
  const dataContext = useData?.() || {};
  const { getFilteredEvents, getFilteredAssignments, markAssignmentCompleted, updateAssignment, updateEvent: _updateEvent, updateCachedAssignment, updateCachedEvent, clearCache, dataRefreshTrigger, deleteAssignment, deleteEvent, deleteEventSeries } = dataContext; // eslint-disable-line no-unused-vars
  
  // Expose DataContext instance globally for vanilla JavaScript integration
  // Store in ref to avoid re-running cleanup on every dataContext change
  const dataContextRef = useRef();
  dataContextRef.current = dataContext;
  
  useEffect(() => {
    // Only set up once on mount
    if (dataContextRef.current && Object.keys(dataContextRef.current).length > 0) {
      window.dataContextInstance = dataContextRef.current;
    }
    
    // Expose create modal handlers globally
    window.openCreateEventModal = (courseId) => {
      setCreateModalCourseId(courseId || null);
      setShowCreateEvent(true);
    };
    window.openCreateAssignmentModal = (courseId) => {
      setCreateModalCourseId(courseId || null);
      setShowCreateAssignment(true);
    };
    window.openCourseCalendarAddModal = (courseId) => {
      setCreateModalCourseId(courseId || null);
      setShowCourseCalendarAdd(true);
    };
    
    return () => {
      // Clean up only on unmount
      delete window.openCreateEventModal;
      delete window.openCreateAssignmentModal;
      delete window.openCourseCalendarAddModal;
      delete window.dataContextInstance;
    };
  }, []); // Empty deps - run once on mount, cleanup on unmount
  
  // Keep window reference updated without triggering cleanup
  useEffect(() => {
    if (window.dataContextInstance && dataContextRef.current) {
      window.dataContextInstance = dataContextRef.current;
    }
  });
  
  // (Vanilla assignment update events are handled by DataContext cache invalidation)

  // Listen for vanilla JS card clicks to open modals
  useEffect(() => {
    const handleOpenAssignmentModal = (event) => {
      if (event.detail?.assignment) {
        setSelectedEvent(null);        // mutual exclusion — close any open event modal
        setSelectedAssignment(event.detail.assignment);
      }
    };

    const handleOpenEventModal = (event) => {
      if (event.detail?.event) {
        setSelectedAssignment(null);   // mutual exclusion — close any open assignment modal
        setSelectedEvent(normalizeEventForModal(event.detail.event));
      }
    };

    window.addEventListener('scoutiq:openAssignmentModal', handleOpenAssignmentModal);
    window.addEventListener('scoutiq:openEventModal', handleOpenEventModal);

    return () => {
      window.removeEventListener('scoutiq:openAssignmentModal', handleOpenAssignmentModal);
      window.removeEventListener('scoutiq:openEventModal', handleOpenEventModal);
    };
  }, []);

  
  const [loadedCourseEvents, setLoadedCourseEvents] = useState([]);
  const [loadedCourseAssignments, setLoadedCourseAssignments] = useState([]);
  const selectedCourseIdRef = useRef(selectedCourseId);

  useEffect(() => { selectedCourseIdRef.current = selectedCourseId; }, [selectedCourseId]);

  useEffect(() => {
    if (!selectedCourseId || !getFilteredEvents || !getFilteredAssignments) {
      setLoadedCourseEvents([]);
      setLoadedCourseAssignments([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        console.log('[ScoutIQDashboard] Fetching assignments for course:', selectedCourseId);
        // Fetch ALL events and assignments without date restrictions
        const eventsParams = {
          endeavorId: selectedCourseId
        };
        const assignmentsParams = {
          endeavorId: selectedCourseId
        };
        const evList = await getFilteredEvents(eventsParams);
        const asgList = await getFilteredAssignments(assignmentsParams);
        console.log('[ScoutIQDashboard] Fetched assignments:', {
          count: asgList.length,
          assignmentIds: asgList.map(a => a.id || a.assignmentId)
        });
        if (cancelled || selectedCourseIdRef.current !== selectedCourseId) return;
        setLoadedCourseEvents(evList);
        setLoadedCourseAssignments(asgList);
        console.log('[ScoutIQDashboard] Updated loaded assignments state');
      } catch (e) {
        console.error('[ScoutIQDashboard] Error fetching assignments:', e);
        if (!cancelled) {
          setLoadedCourseEvents([]);
          setLoadedCourseAssignments([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCourseId, getFilteredEvents, getFilteredAssignments, dataRefreshTrigger]);

  // Prefer externally provided arrays (if non-empty) else loaded ones
  const effectiveCourseEvents = (selectedCourseEvents && selectedCourseEvents.length ? selectedCourseEvents : loadedCourseEvents);
  const effectiveCourseAssignments = (selectedCourseAssignments && selectedCourseAssignments.length ? selectedCourseAssignments : loadedCourseAssignments);

  // Listen for back button custom event to clear selection
  useEffect(() => {
    const handler = () => { if (typeof onSelectCourse === 'function') onSelectCourse(null); };
    window.addEventListener('scoutiq:clearSelection', handler);
    return () => window.removeEventListener('scoutiq:clearSelection', handler);
  }, [onSelectCourse]);

  // When a course is selected, inject vanilla template in #scoutiq-course-view
  // Track target container element for course documents portal
  const docsContainerRef = useRef(null);
  // Track target container element for syllabus preview portal
  const syllabusContainerRef = useRef(null);
  // Track target container element for course calendar portal
  const calendarContainerRef = useRef(null);
  // Track target container element for course info/details portal
  const courseInfoContainerRef = useRef(null);
  // Track target container element for course notes portal
  const notesContainerRef = useRef(null);
  // Track target container element for unified activities portal
  const activitiesContainerRef = useRef(null);
  // Track target container element for course chat portal
  const chatContainerRef = useRef(null);
  // Store vanilla course data extracted from card dataset
  const [vanillaCourseData, setVanillaCourseData] = useState(null);

  // Establish (or reuse) React portal mount inside vanilla documents container
  const ensureDocsPortalMount = (courseId, contextLabel) => {
    const container = document.getElementById('scoutiq-course-docs-container');
    console.log('[ScoutIQ] ensureDocsPortalMount called for course:', courseId, 'container:', container, 'context:', contextLabel);
    if (!container) {
      console.log(`ScoutIQ: Documents container not found (${contextLabel})`);
      return false;
    }
    let mount = container.querySelector('.react-docs-root');
    if (!mount) {
      console.log('[ScoutIQ] Creating new React portal mount');
      mount = document.createElement('div');
      mount.className = 'react-docs-root';
      // Clear any placeholder content first
      container.innerHTML = '';
      container.appendChild(mount);
    } else {
      console.log('[ScoutIQ] Reusing existing React portal mount');
    }
    if (docsContainerRef.current !== mount) {
      console.log('[ScoutIQ] Setting docsContainerRef to:', mount);
      docsContainerRef.current = mount;
      // Force React to re-render by triggering a state update
      setPortalMountKey(prev => prev + 1);
    }
    return true;
  };

  // Establish (or reuse) React portal mount inside vanilla syllabus container
  const ensureSyllabusPortalMount = (courseId, contextLabel) => {
    const container = document.querySelector('.syllabus-preview-container[data-portal-ready="true"]');
    if (!container) {
      return false;
    }
    let mount = container.querySelector('.react-syllabus-root');
    if (!mount) {
      mount = document.createElement('div');
      mount.className = 'react-syllabus-root';
      mount.style.height = '100%';
      mount.style.display = 'flex';
      mount.style.flexDirection = 'column';
      // Only clear placeholder content if mount doesn't exist yet
      // (Don't clear if React portal is already rendering inside)
      container.innerHTML = '';
      container.appendChild(mount);
    }
    if (syllabusContainerRef.current !== mount) {
      syllabusContainerRef.current = mount;
      // Trigger a re-render so createPortal targets the updated mount node
      setPortalMountKey(prev => prev + 1);
    }
    return true;
  };

  // Establish (or reuse) React portal mount inside vanilla calendar container
  const ensureCalendarPortalMount = (courseId, contextLabel) => {
    const container = document.getElementById('scoutiq-course-calendar-container');
    if (!container) {
      return false;
    }
    let mount = container.querySelector('.react-calendar-root');
    if (!mount) {
      mount = document.createElement('div');
      mount.className = 'react-calendar-root';
      mount.style.height = '100%';
      mount.style.display = 'flex';
      mount.style.flexDirection = 'column';
      container.innerHTML = '';
      container.appendChild(mount);
    }
    if (calendarContainerRef.current !== mount) {
      calendarContainerRef.current = mount;
      setPortalMountKey(prev => prev + 1);
    }
    return true;
  };

  // Establish (or reuse) React portal mount inside vanilla course info container
  const ensureCourseInfoPortalMount = (courseId, contextLabel) => {
    const container = document.getElementById('course-info-react-portal');
    if (!container) {
      return false;
    }
    // Mark as ready and use as mount point directly
    container.setAttribute('data-portal-ready', 'true');
    if (courseInfoContainerRef.current !== container) {
      courseInfoContainerRef.current = container;
      setPortalMountKey(prev => prev + 1);
    }
    return true;
  };

  // Establish (or reuse) React portal mount inside vanilla notes container
  const ensureNotesPortalMount = (courseId, contextLabel) => {
    const container = document.getElementById('notes-content');
    if (!container) {
      return false;
    }
    let mount = container.querySelector('.react-notes-root');
    if (!mount) {
      mount = document.createElement('div');
      mount.className = 'react-notes-root';
      mount.style.height = '100%';
      mount.style.display = 'flex';
      mount.style.flexDirection = 'column';
      container.innerHTML = '';
      container.appendChild(mount);
    }
    if (notesContainerRef.current !== mount) {
      notesContainerRef.current = mount;
      setPortalMountKey(prev => prev + 1);
    }
    return true;
  };

  // Establish (or reuse) React portal mount for unified activities inside vanilla Activities tab
  const ensureActivitiesPortalMount = (courseId, contextLabel) => {
    const container = document.getElementById('scoutiq-activities-portal');
    if (!container) {
      return false;
    }
    let mount = container.querySelector('.react-activities-root');
    if (!mount) {
      mount = document.createElement('div');
      mount.className = 'react-activities-root';
      mount.style.height = '100%';
      container.innerHTML = '';
      container.appendChild(mount);
    }
    container.setAttribute('data-portal-ready', 'true');
    if (activitiesContainerRef.current !== mount) {
      activitiesContainerRef.current = mount;
      setPortalMountKey(prev => prev + 1);
    }
    return true;
  };

  // Establish (or reuse) React portal mount for course chat inside vanilla Chat tab
  const ensureChatPortalMount = (courseId, contextLabel) => {
    const container = document.getElementById('scoutiq-course-chat-container');
    if (!container) {
      return false;
    }
    let mount = container.querySelector('.react-chat-root');
    if (!mount) {
      mount = document.createElement('div');
      mount.className = 'react-chat-root';
      mount.style.height = '100%';
      mount.style.display = 'flex';
      mount.style.flexDirection = 'column';
      container.innerHTML = '';
      container.appendChild(mount);
    }
    if (chatContainerRef.current !== mount) {
      chatContainerRef.current = mount;
      setPortalMountKey(prev => prev + 1);
    }
    return true;
  };

  useEffect(() => {
    const mainView = document.getElementById('scoutiq-main-view');
    const courseView = document.getElementById('scoutiq-course-view');
    if (!mainView || !courseView) {
      return;
    }
    if (selectedCourseId) {

      // Check if course view already exists and just needs a data update (not full rebuild)
      // IMPORTANT: Check BEFORE toggling visibility so isAlreadyShowing detects correctly
      const existingCourseId = courseView.getAttribute('data-current-course-id');
      const isAlreadyShowing = existingCourseId === String(selectedCourseId) && !courseView.classList.contains('hidden');
      
      console.log('[ScoutIQ] Course switch check:', {
        selectedCourseId,
        existingCourseId,
        isHidden: courseView.classList.contains('hidden'),
        isAlreadyShowing
      });
      
      // If we're already showing this course, just update the data without rebuilding
      if (isAlreadyShowing) {
        console.log('[ScoutIQ Dashboard] Course already showing, refreshing via DataContext');
        
        // Update the global assignments array so refreshAssignments reads fresh data
        if (effectiveCourseAssignments && effectiveCourseAssignments.length) {
          const normAssignments = effectiveCourseAssignments.map(a => ({
            ...a,
            type: 'Assignment',
            title: a.title || a.name || a.assignmentName || a.endeavorName || a.taskName || a.label || 'Untitled Assignment',
            dueDatetime: a.dueDatetime || a.dueDate || a.dueAt || a.due_at || a.due || a.startTime || a.date || a.timestamp || '',
            app: a.app || a.source || a.platform || a.origin || null,
            completed: a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed',
            endeavorName: a.courseName || a.endeavorName || a.course?.name || null
          }));
          window.scoutiqEffectiveAssignments = normAssignments;
        }

        // Update the global events array so refreshEvents reads fresh data
        if (effectiveCourseEvents && effectiveCourseEvents.length) {
          const normEvents = effectiveCourseEvents.map(e => ({
            ...e,
            type: 'Event',
            title: e.title || e.name || e.eventName || e.assignmentName || e.endeavorName || 'Untitled Event',
            startDatetime: e.startDatetime || e.eventDate || e.startDate || e.dueDate || e.due || e.date || e.datetime || e.timestamp || '',
            app: e.app || e.source || e.platform || e.origin || null,
            endeavorName: e.courseName || e.endeavorName || e.course?.name || null
          }));
          window.scoutiqEffectiveEvents = normEvents;
        }
        
        // Ensure portals are mounted (safe to call multiple times)
        ensureDocsPortalMount(selectedCourseId, 'already-showing');
        ensureSyllabusPortalMount(selectedCourseId, 'already-showing');
        ensureCalendarPortalMount(selectedCourseId, 'already-showing');
        ensureCourseInfoPortalMount(selectedCourseId, 'already-showing');
        ensureNotesPortalMount(selectedCourseId, 'already-showing');
        ensureActivitiesPortalMount(selectedCourseId, 'already-showing');
        ensureChatPortalMount(selectedCourseId, 'already-showing');
        
        // Trigger smooth refreshes that won't destroy the UI
        if (window.scoutiqRefreshAssignments) {
          window.scoutiqRefreshAssignments();
        }
        if (window.scoutiqRefreshEvents) {
          window.scoutiqRefreshEvents();
        }
        return;
      }
      
      const card = document.querySelector(`.scoutiq-course-card[data-course-id="${CSS.escape(String(selectedCourseId))}"]`);
      if (!card) {
        console.warn('[ScoutIQ] Course card not found for ID:', selectedCourseId);
        mainView.classList.remove('hidden');
        courseView.classList.add('hidden');
        return;
      }
      if (card) {
        const data = {
          id: card.dataset.courseId,
          endeavorId: card.dataset.courseId,
          title: card.dataset.courseTitle,
          location: card.dataset.courseLocation,
          dates: card.dataset.courseDates,
          professor: card.dataset.courseProfessor,
          description: card.dataset.courseDescription,
          officeHours: card.dataset.officeHours || card.dataset.courseOfficeHours || '',
          courseCode: card.dataset.courseCode || ''
        };
        // Store for React portal
        setVanillaCourseData(data);
        // Inject vanilla markup first
        showScoutIQCourseView(data, { assignments: effectiveCourseAssignments, events: effectiveCourseEvents });
        // Capture docs container for portal mounting (React context preserved via portal)
        // Try immediate mount after vanilla injection
        setTimeout(() => { 
          ensureDocsPortalMount(selectedCourseId, 'immediate');
          // Also try to mount syllabus portal
          ensureSyllabusPortalMount(selectedCourseId, 'immediate');
          // Also try to mount calendar portal
          ensureCalendarPortalMount(selectedCourseId, 'immediate');
          // Also try to mount course info portal
          ensureCourseInfoPortalMount(selectedCourseId, 'immediate');
          // Also try to mount notes portal
          ensureNotesPortalMount(selectedCourseId, 'immediate');
          // Mount unified activities portal
          ensureActivitiesPortalMount(selectedCourseId, 'immediate');
          ensureChatPortalMount(selectedCourseId, 'immediate');
        }, 10);
        // Also keep the RAF attempt as backup
        requestAnimationFrame(() => { 
          if (!docsContainerRef.current) {
            ensureDocsPortalMount(selectedCourseId, 'raf');
          }
          if (!syllabusContainerRef.current) {
            ensureSyllabusPortalMount(selectedCourseId, 'raf');
          }
          if (!calendarContainerRef.current) {
            ensureCalendarPortalMount(selectedCourseId, 'raf');
          }
          if (!courseInfoContainerRef.current) {
            ensureCourseInfoPortalMount(selectedCourseId, 'raf');
          }
          if (!notesContainerRef.current) {
            ensureNotesPortalMount(selectedCourseId, 'raf');
          }
          if (!activitiesContainerRef.current) {
            ensureActivitiesPortalMount(selectedCourseId, 'raf');
          }
          if (!chatContainerRef.current) {
            ensureChatPortalMount(selectedCourseId, 'raf');
          }
        });
      }
      mainView.classList.add('hidden');
      courseView.classList.remove('hidden');
    } else {
      courseView.classList.add('hidden');
      mainView.classList.remove('hidden');
      // Release portal container refs so React children unmount cleanly
      docsContainerRef.current = null;
      syllabusContainerRef.current = null;
      calendarContainerRef.current = null;
      courseInfoContainerRef.current = null;
      notesContainerRef.current = null;
      activitiesContainerRef.current = null;
      chatContainerRef.current = null;
      setVanillaCourseData(null);
      // Clean stale vanilla markup to prevent flash if user rapidly re-selects another course.
      // (Content will be regenerated by showScoutIQCourseView on next selection.)
      try { courseView.innerHTML = ''; } catch { /* ignore */ }
    }
  }, [selectedCourseId, effectiveCourseAssignments, effectiveCourseEvents]);

  // Reset active tab and clear document preview when selectedCourseId changes
  useEffect(() => {
    setActiveCourseTab('description'); // Reset to default tab when course changes
  }, [selectedCourseId]);

  // Listen for tab changes in the vanilla course view
  useEffect(() => {
    const handleTabChange = (e) => {
      const tabName = e.detail?.tabName;
      if (tabName) {
        console.log('[ScoutIQ] Active tab changed to:', tabName);
        setActiveCourseTab(tabName);
        // Mark syllabus as visited when tab is clicked
        if (tabName === 'syllabus' && selectedCourseId) {
          setSyllabusTabVisitedCourses(prev => new Set(prev).add(selectedCourseId));
        }
      }
    };
    
    // Listen for custom tab change events from vanilla code
    window.addEventListener('scoutiq:tab-changed', handleTabChange);
    
    // Also detect tab clicks directly
    const courseView = document.getElementById('scoutiq-course-view');
    if (courseView) {
      const handleTabClick = (e) => {
        const tab = e.target.closest('.course-tab');
        if (tab && tab.dataset.tab) {
          console.log('[ScoutIQ] Tab clicked:', tab.dataset.tab);
          setActiveCourseTab(tab.dataset.tab);
          // Mark syllabus as visited when tab is clicked
          if (tab.dataset.tab === 'syllabus' && selectedCourseId) {
            setSyllabusTabVisitedCourses(prev => new Set(prev).add(selectedCourseId));
          }
        }
      };
      courseView.addEventListener('click', handleTabClick);
      
      return () => {
        window.removeEventListener('scoutiq:tab-changed', handleTabChange);
        courseView.removeEventListener('click', handleTabClick);
      };
    }
    
    return () => {
      window.removeEventListener('scoutiq:tab-changed', handleTabChange);
    };
  }, [selectedCourseId]);

  // Ensure syllabus portal mount is ready when switching to syllabus tab
  useEffect(() => {
    if (activeCourseTab === 'syllabus' && selectedCourseId) {
      // Mark that syllabus tab has been visited (triggers lazy load)
      setSyllabusTabVisitedCourses(prev => new Set(prev).add(selectedCourseId));
      
      // Give the DOM a moment to update after tab switch
      setTimeout(() => {
        ensureSyllabusPortalMount(selectedCourseId, 'tab-switch');
      }, 50);
    }
  }, [activeCourseTab, selectedCourseId]);

  // === Retry logic: after course selection, poll for documents & syllabus containers up to 12 attempts (progressive backoff) ===
  useEffect(() => {
    if (!selectedCourseId) return;
    let attempts = 0;
    let cancelled = false;
    const poll = () => {
      if (cancelled) return; // cancelled
      attempts += 1;
      
      let docsMounted = false;
      let syllabusMounted = false;
      
      // Try to mount documents portal
      if (!docsContainerRef.current) {
        if (ensureDocsPortalMount(selectedCourseId, 'poll-' + attempts)) {
          docsMounted = true;
        }
      } else {
        docsMounted = true;
      }
      
      // Try to mount syllabus portal (container should always exist now)
      if (!syllabusContainerRef.current) {
        if (ensureSyllabusPortalMount(selectedCourseId, 'poll-' + attempts)) {
          syllabusMounted = true;
        }
      } else {
        syllabusMounted = true;
      }
      
      // Try to mount calendar portal
      let calendarMounted = false;
      if (!calendarContainerRef.current) {
        if (ensureCalendarPortalMount(selectedCourseId, 'poll-' + attempts)) {
          calendarMounted = true;
        }
      } else {
        calendarMounted = true;
      }
      
      // Try to mount course info portal
      let courseInfoMounted = false;
      if (!courseInfoContainerRef.current) {
        if (ensureCourseInfoPortalMount(selectedCourseId, 'poll-' + attempts)) {
          courseInfoMounted = true;
        }
      } else {
        courseInfoMounted = true;
      }
      
      // Try to mount notes portal
      let notesMounted = false;
      if (!notesContainerRef.current) {
        if (ensureNotesPortalMount(selectedCourseId, 'poll-' + attempts)) {
          notesMounted = true;
        }
      } else {
        notesMounted = true;
      }
      
      // Try to mount chat portal
      let chatMounted = false;
      if (!chatContainerRef.current) {
        if (ensureChatPortalMount(selectedCourseId, 'poll-' + attempts)) {
          chatMounted = true;
        }
      } else {
        chatMounted = true;
      }
      
      // Continue polling if any portal is not yet mounted
      if ((!docsMounted || !syllabusMounted || !calendarMounted || !courseInfoMounted || !notesMounted || !chatMounted) && attempts < 12) {
        const nextDelay = attempts < 5 ? 100 * attempts : 200 + 75 * attempts;
        setTimeout(poll, nextDelay);
      }
    };
    // Initial attempt with slight delay to allow vanilla course view to render
    setTimeout(poll, 50);
    return () => { cancelled = true; };
  }, [selectedCourseId]);

  // Tools Hub interaction - simplified for always-visible tools
  useEffect(() => {
    const toolButtons = document.querySelectorAll('.scout-tool-btn');
    const handlers = new Map();

    toolButtons.forEach(btn => {
      const handleToolClick = () => {
        const toolName = btn.dataset?.tool || '';
        const normalizedTool = normalizeToolKey(toolName);
        // Note: Subscription gating is handled by the tool components themselves
        // (PodcastsTool and AudiobooksTool show upgrade prompts for Basic/Free users)
        setActiveTool(normalizedTool);
      };
      handlers.set(btn, handleToolClick);
      btn.addEventListener('click', handleToolClick);
    });

    return () => {
      toolButtons.forEach(btn => {
        const handler = handlers.get(btn);
        if (handler) {
          btn.removeEventListener('click', handler);
        }
      });
      handlers.clear();
    };
  }, []);

  // Update tool button active states based on activeTool
  useEffect(() => {
    const resolvedTool = normalizeToolKey(activeTool);
    const toolButtons = document.querySelectorAll('.scout-tool-btn');
    toolButtons.forEach(btn => {
      const toolName = normalizeToolKey(btn.dataset?.tool || '');
      if (toolName === resolvedTool) {
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }, [activeTool]);

  // Robust endeavorId resolver for documents: prefer explicit endeavorId, fall back to id variants
  const resolveEndeavorId = (courseObj, selectedId) => {
    if (!courseObj && selectedId) return selectedId;
    return (
      courseObj?.endeavorId ||
      courseObj?.endeavorID ||
      courseObj?.id ||
      courseObj?.courseId ||
      courseObj?.courseID ||
      selectedId || null
    );
  };
  const resolvedEndeavorId = resolveEndeavorId(selectedCourse, selectedCourseId);

  // Robust course-name resolver: checks multiple sources so the chat title never falls back to 'Course'
  const resolvedCourseName = useMemo(() => {
    // 1) vanillaCourseData comes from the DOM card dataset — most reliable for the current view
    if (vanillaCourseData?.title) return vanillaCourseData.title;
    // 2) selectedCourse prop from parent
    if (selectedCourse?.title) return selectedCourse.title;
    if (selectedCourse?.name) return selectedCourse.name;
    if (selectedCourse?.endeavorName) return selectedCourse.endeavorName;
    // 3) Look up from full courses array by ID
    if (selectedCourseId && courses?.length) {
      const found = courses.find(c => {
        const cid = c?.id || c?.endeavorId || c?.courseId || c?.courseID || c?.endeavorID;
        return cid && String(cid) === String(selectedCourseId);
      });
      if (found) return found.title || found.name || found.endeavorName || null;
    }
    return null;
  }, [vanillaCourseData, selectedCourse, selectedCourseId, courses]);

  // Get syllabus document info from the syllabus container (memoized to prevent unnecessary remounts)
  const syllabusDocInfo = useMemo(() => {
    if (!selectedCourseId) return { docId: null, docName: null };
    
    // First check if the syllabus container exists with the data
    const syllabusContainer = document.querySelector('.syllabus-preview-container[data-portal-ready="true"]');
    if (syllabusContainer) {
      // Try both camelCase (dataset.syllabusDocId) and explicit getAttribute
      const docId = syllabusContainer.dataset.syllabusDocId || 
                    syllabusContainer.getAttribute('data-syllabus-doc-id') || 
                    null;
      const docName = syllabusContainer.dataset.syllabusDocName || 
                      syllabusContainer.getAttribute('data-syllabus-doc-name') || 
                      'Syllabus';
      console.log('ScoutIQ: Extracted syllabus info from syllabus container:', {
        courseId: selectedCourseId,
        docId: docId,
        docName: docName,
        hasDocId: !!docId,
        containerFound: true,
        datasetKeys: Object.keys(syllabusContainer.dataset),
        fullDataset: {...syllabusContainer.dataset},
        rawDocIdAttr: syllabusContainer.getAttribute('data-syllabus-doc-id'),
        rawDocNameAttr: syllabusContainer.getAttribute('data-syllabus-doc-name'),
        outerHTML: syllabusContainer.outerHTML.substring(0, 300)
      });
      return { docId, docName };
    }
    
    // Fallback: try reading from course card if container not yet populated
    const card = document.querySelector(`.scoutiq-course-card[data-course-id="${CSS.escape(String(selectedCourseId))}"]`);
    if (!card) {
      console.log('ScoutIQ: No course card or syllabus container found for syllabus info extraction');
      return { docId: null, docName: null };
    }
    const info = {
      docId: card.dataset.courseSyllabusDocId || null,
      docName: card.dataset.courseSyllabusDocName || 'Syllabus'
    };
    console.log('ScoutIQ: Extracted syllabus info from course card (fallback):', {
      courseId: selectedCourseId,
      docId: info.docId,
      docName: info.docName,
      hasDocId: !!info.docId,
      containerFound: false
    });
    return info;
  }, [selectedCourseId]);

  // Modal handlers for assignment and event details
  // onUpdate: UI-only — updates modal display immediately (called by useDetailsModalSave for optimistic updates)
  const handleAssignmentUpdate = useCallback((updatedAssignmentData) => {
    setSelectedAssignment(updatedAssignmentData);
  }, []);

  // onSaved: called AFTER API success — surgically updates cache without resetting dashboard
  const handleAssignmentSaved = useCallback(async (serverData, id) => {
    try {
      // Surgically update the changed entity in all cache entries (no full cache clear)
      if (updateCachedAssignment && serverData) {
        updateCachedAssignment(id, serverData);
      }
      // Bump refresh trigger so dashboard useEffect re-reads from updated cache
      // (This is a lightweight operation — cache is already updated, no network call)
      window.dispatchEvent(new Event('assignments:updated'));
    } catch (err) {
      console.warn('Post-save cache update failed (non-fatal):', err);
    }
  }, [updateCachedAssignment]);

  const handleAssignmentMarkComplete = useCallback(async (assignmentData) => {
    try {
      const id = assignmentData.assignmentId || assignmentData.id;
      const willBeCompleted = assignmentData.completed;
      let response;
      if (willBeCompleted && markAssignmentCompleted) {
        response = await markAssignmentCompleted(id);
      } else {
        response = await updateAssignment(id, assignmentData);
      }
      if (!response?.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Failed to update completion status: ${errorText}`);
      }
      if (clearCache) {
        clearCache('assignments');
        clearCache(`assignment_${id}`);
      }
      setSelectedAssignment(null);
    } catch (error) {
      console.error('Assignment completion toggle failed:', error);
      throw error;
    }
  }, [markAssignmentCompleted, updateAssignment, clearCache]);

  // onUpdate: UI-only — updates modal display immediately
  const handleEventUpdate = useCallback((updatedEventData) => {
    setSelectedEvent(updatedEventData);
  }, []);

  // onSaved: called AFTER API success — surgically updates cache without resetting dashboard
  const handleEventSaved = useCallback(async (serverData, id) => {
    try {
      // Surgically update the changed entity in all cache entries (no full cache clear)
      if (updateCachedEvent && serverData) {
        updateCachedEvent(id, serverData);
      }
      // Dispatch event for event cards and other listeners
      window.dispatchEvent(new CustomEvent('scoutiq:eventUpdated', { 
        detail: { 
          eventId: id, 
          courseId: selectedCourseId,
          updatedEvent: serverData
        } 
      }));
    } catch (err) {
      console.warn('Post-save cache update failed (non-fatal):', err);
    }
  }, [updateCachedEvent, selectedCourseId]);

  return (
    <div className="scoutiq-dashboard-container" aria-label="ScoutIQ dashboard skeleton">
      <div className="scoutiq-dashboard-frame">
        {/* Background syllabus scan notification banner */}
        <SyllabusScanBanner />
        {/* ARIA live region for screen reader announcements */}
        <div className="srOnly" role="status" aria-live="polite" aria-atomic="true">
          {liveMessage}
        </div>
        
        <div 
          className={cn(
            "scoutiq-dashboard-grid",
            transitionState === 'transitioning' && "isTransitioning"
          )}
          data-tour="dashboard-layout"
        >
          {/* Left column: My Courses with edge control */}
          <div 
            className="courses-column" 
            role="complementary"
            id="courses-panel"
            data-tutorial="my-courses-panel"
            data-tutorial-legacy="panel-left"
          >
            <CoursesColumn
              courses={stableCourses}
              loading={loadingCourses}
              selectedCourseId={selectedCourseId}
              onSelectCourse={onSelectCourse}
              highlightCourseId={highlightCourseId}
              isCollapsed={false}
              isDrawerMode={false}
              isDrawerOpen={false}
              isResizing={false}
              transitionState={transitionState}
              prefersReducedMotion={prefersReducedMotion}
              onToggleCollapse={() => {}}
              onCloseDrawer={() => {}}
              focusTargetRef={focusTargetRefs.courses}
              setLiveMessage={setLiveMessage}
            />
          </div>
        <div className="center-column" role="main" data-tutorial="center-panel" data-tutorial-legacy="panel-center">
          {/* Always render center intelligence view for consistency across chat expansion states */}
          <div id="scoutiq-main-view">
            <CenterColumn
              selectedCourse={selectedCourse}
              selectedCourseAssignments={effectiveCourseAssignments}
              selectedCourseEvents={effectiveCourseEvents}
              selectedCourseSummary={selectedCourseSummary}
              userName={userName}
              upcomingItems={upcomingItems}
              onTimelineReady={handleTimelineReady}
            />
          </div>
          {/* Vanilla injected course view (hidden until a course is clicked) */}
          <div id="scoutiq-course-view" className="hidden" />
          
          {/* Portal mount point for Documents tab: when a course is selected & vanilla template injected */}
          {selectedCourseId && docsContainerRef.current && createPortal(
            <div key={`portal-docs-${selectedCourseId}-${portalMountKey}`} className="course-documents-react-embed" data-endeavor-id={resolvedEndeavorId}>
              {console.log('[ScoutIQ Portal] Rendering documents portal for course:', selectedCourseId, 'endeavorId:', resolvedEndeavorId)}
              <section className="scoutiq-docs-section">
                  <div className="scoutiq-docs-upload">
                    <div className="scoutiq-docs-card scoutiq-upload-card">
                      <h3 className="scoutiq-docs-card-title">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="17 8 12 3 7 8"></polyline>
                          <line x1="12" y1="3" x2="12" y2="15"></line>
                        </svg>
                        Upload Files
                      </h3>
                      <FileUpload endeavorId={resolvedEndeavorId} scoutIQMode={true} />
                    </div>
                  </div>
                  <div className="scoutiq-docs-my-docs">
                    <div className="scoutiq-docs-card scoutiq-my-documents-card">
                      <h3 className="scoutiq-docs-card-title">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        My Documents
                      </h3>
                      <MyDocumentsCard 
                        endeavorId={resolvedEndeavorId} 
                        maxItems={100}
                        onDocumentClick={handleDocumentClick}
                        scoutIQMode={true}
                      />
                    </div>
                  </div>
                </section>
            </div>,
            docsContainerRef.current
          )}
          
          {/* Portal mount point for Syllabus preview: when a course is selected & syllabus exists */}
          {(() => {
            const shouldRenderPortal = selectedCourseId && 
                                      syllabusContainerRef.current && 
                                      syllabusDocInfo.docId;
            
            if (!shouldRenderPortal) return null;
            
            const hasSyllabusBeenVisited = syllabusTabVisitedCourses.has(selectedCourseId);
            console.log('[ScoutIQ] Rendering syllabus portal - visited:', hasSyllabusBeenVisited, 'for course:', selectedCourseId);
            
            return createPortal(
              <div key={`portal-syllabus-${selectedCourseId}`} className="course-syllabus-react-embed">
                {hasSyllabusBeenVisited ? (
                  <DocumentPreview 
                    documentId={syllabusDocInfo.docId}
                    documentName={syllabusDocInfo.docName}
                    className="scoutiq-syllabus-preview"
                  />
                ) : (
                  <div className="syllabus-placeholder" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: '400px',
                    color: 'var(--semantic-text-muted)',
                    fontSize: '14px'
                  }}>
                    Click the Syllabus tab to view the document
                  </div>
                )}
              </div>,
              syllabusContainerRef.current
            );
          })()}

          {/* Portal mount point for Calendar: when a course is selected, show calendar filtered to that course */}
          {selectedCourseId && calendarContainerRef.current && createPortal(
            <div key={`portal-calendar-${selectedCourseId}-${portalMountKey}`} className="course-detail-calendar scoutiq-calendar-layout">
              <div className="course-calendar-react-embed">
                <Calendar
                  mode="course"
                  headerTitle={selectedCourse?.title ? `${selectedCourse.title} Calendar` : 'Course Calendar'}
                  scopeEndeavorId={selectedCourseId}
                  courseEvents={effectiveCourseEvents}
                  courseAssignments={effectiveCourseAssignments}
                  enableSplitLayout={true}
                />
              </div>
            </div>,
            calendarContainerRef.current
          )}

          {/* Portal mount point for Course Info: React-based inline editing for course details */}
          {selectedCourseId && (vanillaCourseData || selectedCourse) && courseInfoContainerRef.current && createPortal(
            <div key={`portal-course-info-${selectedCourseId}`} className="course-info-react-embed">
              <CourseDetails 
                endeavor={vanillaCourseData || selectedCourse}
                events={effectiveCourseEvents}
                onUpdate={(updatedCourse) => {
                  console.log('[ScoutIQ] Course updated:', updatedCourse);
                  // Update vanilla data with new values
                  const merged = vanillaCourseData
                    ? { ...vanillaCourseData, ...updatedCourse }
                    : updatedCourse;
                  setVanillaCourseData(merged);

                  // --- Immediately update vanilla DOM so user sees changes without refresh ---
                  const courseView = document.getElementById('scoutiq-course-view');
                  if (courseView) {
                    // Header title
                    const titleEl = courseView.querySelector('.course-view-title');
                    const newTitle = updatedCourse.title || updatedCourse.name || '';
                    if (titleEl && newTitle) {
                      titleEl.textContent = newTitle;
                    }

                    // Helper: resolve updated field values from metadata + flat fields
                    const getUpdatedMeta = (metaName, flatField) => {
                      const entry = updatedCourse.metadata && updatedCourse.metadata.find(function(m) {
                        var n = ((m.metadataDefinition && m.metadataDefinition.name) || m.metadataName || m.name || '').toLowerCase();
                        return n === metaName.toLowerCase();
                      });
                      return (entry && entry.value) || updatedCourse[flatField] || merged[flatField] || '';
                    };

                    // Rebuild meta chips
                    var metaContainer = courseView.querySelector('#course-header-meta');
                    if (metaContainer) {
                      var code = getUpdatedMeta('Course Code', 'courseCode');
                      var prof = getUpdatedMeta('Professor', 'professor');
                      var loc  = getUpdatedMeta('Course Location', 'location');
                      var esc = function(s) { return s ? s.replace(/</g, '&lt;') : ''; };
                      var chips = '';
                      if (code) chips += '<span class="header-meta-chip code-chip" title="Course code">' + esc(code) + '</span>';
                      if (prof) chips += '<span class="header-meta-chip prof-chip"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>' + esc(prof) + '</span>';
                      if (loc) chips += '<span class="header-meta-chip loc-chip"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>' + esc(loc) + '</span>';
                      metaContainer.innerHTML = chips;
                    }
                  }

                  // --- Update the sidebar course card so the list reflects changes too ---
                  var sideCard = document.querySelector('.scoutiq-course-card[data-course-id="' + CSS.escape(String(selectedCourseId)) + '"]');
                  if (sideCard) {
                    var cardTitle = updatedCourse.title || updatedCourse.name || '';
                    if (cardTitle) {
                      sideCard.setAttribute('data-course-title', cardTitle);
                      var cardTitleEl = sideCard.querySelector('.scoutiq-course-card-header__title');
                      if (cardTitleEl) {
                        cardTitleEl.textContent = cardTitle;
                        cardTitleEl.setAttribute('title', cardTitle);
                      }
                    }
                    if (updatedCourse.professor) {
                      sideCard.setAttribute('data-course-professor', updatedCourse.professor);
                      var profEl = sideCard.querySelector('.scoutiq-course-meta');
                      if (profEl) profEl.textContent = updatedCourse.professor;
                    }
                    if (updatedCourse.location) {
                      sideCard.setAttribute('data-course-location', updatedCourse.location);
                      var roomEl = sideCard.querySelector('.scoutiq-course-room');
                      if (roomEl) roomEl.textContent = updatedCourse.location;
                    }
                    if (updatedCourse.description) {
                      sideCard.setAttribute('data-course-description', updatedCourse.description);
                    }
                    if (updatedCourse.courseCode) {
                      sideCard.setAttribute('data-course-code', updatedCourse.courseCode);
                      var pillEl = sideCard.querySelector('.scoutiq-course-pill');
                      if (pillEl) pillEl.textContent = updatedCourse.courseCode;
                    }
                  }

                  // Trigger data refresh to update course in list
                  if (window.dataContextInstance && window.dataContextInstance.fetchEndeavors) {
                    window.dataContextInstance.fetchEndeavors();
                  }
                }}
              />
            </div>,
            courseInfoContainerRef.current
          )}

          {/* Portal mount point for Notes: React-based notes list/editor for course notes */}
          {selectedCourseId && (vanillaCourseData || selectedCourse) && notesContainerRef.current && (() => {
            console.log('[ScoutIQ] Rendering notes portal:', {
              selectedCourseId,
              courseName: resolvedCourseName || 'Course',
              endeavorId: resolvedEndeavorId,
              hasNotesContainer: !!notesContainerRef.current
            });
            return createPortal(
              <div key={`portal-notes-${selectedCourseId}`} className="course-notes-react-embed">
                <CourseNotesContainer 
                  courseName={resolvedCourseName || 'Course'}
                  endeavorId={resolvedEndeavorId}
                />
              </div>,
              notesContainerRef.current
            );
          })()}

          {/* Portal mount point for Unified Activities: React-based merged timeline for activities tab */}
          {selectedCourseId && activitiesContainerRef.current && createPortal(
            <div key={`portal-activities-${selectedCourseId}-${portalMountKey}`} className="unified-activities-react-embed">
              <UnifiedActivities
                courseEndeavorId={selectedCourseId}
                courseName={resolvedCourseName || 'Course'}
              />
            </div>,
            activitiesContainerRef.current
          )}

          {/* Portal mount point for Course Chat: AiChatCard in endeavor mode */}
          {selectedCourseId && chatContainerRef.current && createPortal(
            <div key={`portal-chat-${selectedCourseId}`} className="course-chat-react-embed">
                <AiChatCard
                  mode="endeavor"
                  endeavorId={resolvedEndeavorId}
                  endeavorName={resolvedCourseName || 'Course'}
                  disableAutoFocus={true}
                  hideHeader
                />
            </div>,
            chatContainerRef.current
          )}

        </div>
        
        {/* Right column: Scout's Desk — disconnected from dashboard layout */}
        
        </div>
      </div>

      {/* Modals for assignment and event details - rendered at dashboard level for vanilla JS course view */}
      <CenteredModal
        isOpen={!!selectedAssignment}
        onClose={() => setSelectedAssignment(null)}
        title={selectedAssignment?.title || selectedAssignment?.name}
        onDelete={selectedAssignment ? async () => {
          const assignmentId = selectedAssignment.assignmentId || selectedAssignment.id;
          setSelectedAssignment(null); // Close immediately
          await deleteAssignment(assignmentId);
        } : undefined}
      >
        {selectedAssignment && (
          <AssignmentDetails
            assignment={selectedAssignment}
            onClose={() => setSelectedAssignment(null)}
            onUpdate={handleAssignmentUpdate}
            onSaved={handleAssignmentSaved}
            onMarkComplete={handleAssignmentMarkComplete}
            onEdit={() => {
              // TODO: wire up assignment editing
            }}
          />
        )}
      </CenteredModal>

      

      <CenteredModal
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        title={selectedEvent?.title || selectedEvent?.name}
        isRecurringEvent={selectedEvent && (selectedEvent.isRecurring || selectedEvent.recurrenceRule || selectedEvent.parentEventId)}
        onDelete={selectedEvent ? async () => {
          const eventId = selectedEvent.eventId || selectedEvent.id;
          setSelectedEvent(null); // Close immediately
          await deleteEvent(eventId);
        } : undefined}
        onDeleteSeries={selectedEvent ? async () => {
          const parentId = selectedEvent.parentEventId || selectedEvent.eventId || selectedEvent.id;
          setSelectedEvent(null); // Close immediately
          await deleteEventSeries(parentId);
        } : undefined}
      >
        {selectedEvent && (
          <EventDetails
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onUpdate={handleEventUpdate}
            onSaved={handleEventSaved}
            onEdit={() => {
              // TODO: wire up event editing
            }}
            onDelete={async (eventId) => {
              await deleteEvent(eventId);
              clearCache('user-events');
              clearCache('user-assignments');
              setSelectedEvent(null);
            }}
          />
        )}
      </CenteredModal>

      {/* Create Event Modal */}
      <CenteredModal
        isOpen={showCreateEvent}
        onClose={() => {
          setShowCreateEvent(false);
          setCreateModalCourseId(null);
        }}
        title="✨ Create New Event"
      >
        <EventForm
          compact={true}
          prefill={createModalCourseId ? { endeavorId: createModalCourseId } : {}}
          onCancel={() => {
            setShowCreateEvent(false);
            setCreateModalCourseId(null);
          }}
          onSuccess={(createdEvent) => {
            // Optimistically update Timeline immediately (before background refetch completes)
            if (timelineUpdatersRef.current?.addEvent) {
              console.log('✅ Calling timeline.addEvent with:', createdEvent);
              timelineUpdatersRef.current.addEvent(createdEvent);
            } else {
              console.warn('⚠️ Timeline addEvent updater not available!');
            }
            
            // Close modal
            setShowCreateEvent(false);
            setCreateModalCourseId(null);
          }}
        />
      </CenteredModal>

      {/* Create Assignment Modal */}
      <CenteredModal
        isOpen={showCreateAssignment}
        onClose={() => {
          setShowCreateAssignment(false);
          setCreateModalCourseId(null);
        }}
        title="✨ Create New Assignment"
      >
        <AssignmentForm
          compact={true}
          prefill={createModalCourseId ? { endeavorId: createModalCourseId } : {}}
          onCancel={() => {
            setShowCreateAssignment(false);
            setCreateModalCourseId(null);
          }}
          onSuccess={(createdAssignment) => {
            // Optimistically update Timeline immediately (before background refetch completes)
            if (timelineUpdatersRef.current?.addAssignment) {
              console.log('✅ Calling timeline.addAssignment with:', createdAssignment);
              timelineUpdatersRef.current.addAssignment(createdAssignment);
            } else {
              console.warn('⚠️ Timeline addAssignment updater not available!');
            }
            
            // Close modal
            setShowCreateAssignment(false);
            setCreateModalCourseId(null);
          }}
        />
      </CenteredModal>

      {/* Course Calendar Add Modal - unified event/assignment creation from course calendar */}
      <AddToCalendarModal
        isOpen={showCourseCalendarAdd}
        onClose={() => {
          setShowCourseCalendarAdd(false);
          setCreateModalCourseId(null);
        }}
        onCreated={({ type, item }) => {
          console.log(`✅ Created ${type} from course calendar:`, item);
          // Optimistically update timeline
          if (type === 'event' && timelineUpdatersRef.current?.addEvent) {
            timelineUpdatersRef.current.addEvent(item);
          } else if (type === 'assignment' && timelineUpdatersRef.current?.addAssignment) {
            timelineUpdatersRef.current.addAssignment(item);
          }
          // Close modal
          setShowCourseCalendarAdd(false);
          setCreateModalCourseId(null);
        }}
        prefill={{
          endeavorId: createModalCourseId || null
        }}
      />
    </div>
  );
}
